"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import ConnectorSelection from "./components/ConnectorSelection"
import ConnectAccounts from "./components/ConnectAccounts"
import ChooseSync from "./components/ChooseSync"
import MapFields from "./components/MapFields"
import SyncDirection from "./components/SyncDirection"
import ReviewSyncSetup from "./components/ReviewSyncSetup"
import SyncingProgress from "./components/SyncingProgress"
import "./SetupFlow.css"

export interface ConnectorConfig {
  id: string
  name: string
  from: string
  to: string
  fromLogo: string
  toLogo: string
}

export interface ConnectionStatus {
  hubspot: boolean
  salesforce: boolean
}

export interface SyncOption {
  id: string
  title: string
  description: string
  selected: boolean
}

export interface FieldMapping {
  id: string
  hubspotField: string
  salesforceField: string
}

export interface SetupData {
  selectedConnector: ConnectorConfig | null
  connectionStatus: ConnectionStatus
  selectedSyncOptions: SyncOption[]
  fieldMappings: FieldMapping[]
  syncDirection: string | null
}

type SetupStep =
  | "connector-selection"
  | "connect-accounts"
  | "choose-sync"
  | "map-fields"
  | "sync-direction"
  | "review-setup"
  | "syncing-progress"

const SetupFlow: React.FC = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState<SetupStep>("connector-selection")
  const [hasExistingConnectors, setHasExistingConnectors] = useState(false)

  const [setupData, setSetupData] = useState<SetupData>({
    selectedConnector: null,
    connectionStatus: {
      hubspot: false,
      salesforce: false,
    },
    selectedSyncOptions: [],
    fieldMappings: [
      { id: "1", hubspotField: "firstname", salesforceField: "FirstName" },
      { id: "2", hubspotField: "lastname", salesforceField: "LastName" },
      { id: "3", hubspotField: "email", salesforceField: "Email" },
      { id: "4", hubspotField: "phone", salesforceField: "Phone" },
      { id: "5", hubspotField: "jobtitle", salesforceField: "Title" },
    ],
    syncDirection: null,
  })

  useEffect(() => {
    const checkExistingConnectors = () => {
      const existingHubSpotConnection = localStorage.getItem("hubspot_connected")
      setHasExistingConnectors(!!existingHubSpotConnection)
    }

    checkExistingConnectors()
  }, [])

  const updateSetupData = (updates: Partial<SetupData>) => {
    setSetupData((prev) => ({ ...prev, ...updates }))
  }

  const goToNextStep = () => {
    const stepOrder: SetupStep[] = [
      "connector-selection",
      "connect-accounts",
      "choose-sync",
      "map-fields",
      "sync-direction",
      "review-setup",
      "syncing-progress",
    ]

    const currentIndex = stepOrder.indexOf(currentStep)
    if (currentIndex < stepOrder.length - 1) {
      setCurrentStep(stepOrder[currentIndex + 1])
    }
  }

  const goToPreviousStep = () => {
    const stepOrder: SetupStep[] = [
      "connector-selection",
      "connect-accounts",
      "choose-sync",
      "map-fields",
      "sync-direction",
      "review-setup",
      "syncing-progress",
    ]

    const currentIndex = stepOrder.indexOf(currentStep)
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1])
    }
  }

  const handleConnectorSubmit = (connector: ConnectorConfig) => {
    updateSetupData({ selectedConnector: connector })
    goToNextStep()
  }

  const handleConnectionSubmit = (status: ConnectionStatus) => {
    updateSetupData({ connectionStatus: status })
    goToNextStep()
  }

  const handleSyncOptionsSubmit = (options: SyncOption[]) => {
    updateSetupData({ selectedSyncOptions: options })
    goToNextStep()
  }

  const handleFieldMappingSubmit = (mappings: FieldMapping[]) => {
    updateSetupData({ fieldMappings: mappings })
    goToNextStep()
  }

  const handleSyncDirectionSubmit = (direction: string) => {
    updateSetupData({ syncDirection: direction })
    goToNextStep()
  }

  const handleReviewSubmit = () => {
    goToNextStep()
  }

  const handleSyncComplete = () => {
    // localStorage.setItem("setup_completed", "true")
    // localStorage.setItem("hubspot_connected", "true")
    navigate("/dashboard")
  }

  const handleAddAnotherConnector = () => {
    setCurrentStep("connector-selection")
    updateSetupData({
      selectedConnector: null,
      connectionStatus: { hubspot: false, salesforce: false },
      selectedSyncOptions: [],
      syncDirection: null,
    })
  }

  const renderCurrentStep = () => {
    switch (currentStep) {
      case "connector-selection":
        return (
          <ConnectorSelection
            hasExistingConnectors={hasExistingConnectors}
            onSubmit={handleConnectorSubmit}
            onAddAnother={handleAddAnotherConnector}
            setupData={setupData}
          />
        )

      case "connect-accounts":
        return (
          <ConnectAccounts
            selectedConnector={setupData.selectedConnector!}
            onSubmit={handleConnectionSubmit}
            onBack={goToPreviousStep}
            setupData={setupData}
          />
        )

      case "choose-sync":
        return <ChooseSync onSubmit={handleSyncOptionsSubmit} onBack={goToPreviousStep} setupData={setupData} />

      case "map-fields":
        return (
          <MapFields
            selectedSyncOptions={setupData.selectedSyncOptions}
            onSubmit={handleFieldMappingSubmit}
            onBack={goToPreviousStep}
            setupData={setupData}
          />
        )

      case "sync-direction":
        return <SyncDirection onSubmit={handleSyncDirectionSubmit} onBack={goToPreviousStep} setupData={setupData} />

      case "review-setup":
        return <ReviewSyncSetup setupData={setupData} onSubmit={handleReviewSubmit} onBack={goToPreviousStep} />

      case "syncing-progress":
        return <SyncingProgress setupData={setupData} onComplete={handleSyncComplete} />

      default:
        return null
    }
  }

  return (
    <div className="setup-flow">
      <div className="setup-progress">
        <div className="progress-steps">
          {[
            { key: "connector-selection", label: "Connector" },
            { key: "connect-accounts", label: "Connect" },
            { key: "choose-sync", label: "Sync Options" },
            { key: "map-fields", label: "Field Mapping" },
            { key: "sync-direction", label: "Direction" },
            { key: "review-setup", label: "Review" },
            { key: "syncing-progress", label: "Sync" },
          ].map((step, index) => {
            const stepOrder: SetupStep[] = [
              "connector-selection",
              "connect-accounts",
              "choose-sync",
              "map-fields",
              "sync-direction",
              "review-setup",
              "syncing-progress",
            ]
            const currentIndex = stepOrder.indexOf(currentStep)
            const isActive = step.key === currentStep
            const isCompleted = index < currentIndex

            return (
              <div
                key={step.key}
                className={`progress-step ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
              >
                <div className="step-indicator">
                  {isCompleted ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20,6 9,17 4,12" />
                    </svg>
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>
                <span className="step-label">{step.label}</span>
              </div>
            )
          })}
        </div>
      </div>

      <div className="setup-content">{renderCurrentStep()}</div>
    </div>
  )
}

export default SetupFlow
